import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Network, Globe, BarChart3, ShieldCheck, Lock, Users, 
  ArrowRight, Settings, CheckCircle2, Building2, Layers
} from 'lucide-react';

const DSOPage = () => {
  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-600/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

        <div className="container mx-auto max-w-7xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-400/20 text-blue-300 text-xs font-bold uppercase tracking-wider mb-6">
            <Building2 className="w-4 h-4" />
            Enterprise Solutions
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Scale Revenue, <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
              Not Headcount
            </span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
            The only AI platform purpose-built for multi-location dental groups. Centralize operations, standardize patient experience, and unlock organizational intelligence across 5 or 500 locations.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book-demo">
              <button className="px-8 py-4 rounded-xl font-bold text-base transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-500/30 transform hover:-translate-y-1">
                Book an Enterprise Demo
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Key Value Props */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Top DSOs Choose Asha
            </h2>
            <p className="text-lg text-gray-600">
              Single-practice tools break at scale. We built our platform to handle the complexity of multi-location organizations from day one.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Feature 1 */}
            <div className="group p-8 rounded-3xl bg-gray-50 border border-gray-100 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-900/5 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Globe className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Unlimited Scalability</h3>
              <p className="text-gray-600 leading-relaxed">
                Whether you're acquiring one practice or ten, deploy our AI agents instantly. Standardization allows new locations to operate at peak efficiency from day one.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="group p-8 rounded-3xl bg-gray-50 border border-gray-100 hover:border-purple-200 hover:shadow-xl hover:shadow-purple-900/5 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Layers className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Centralized Control</h3>
              <p className="text-gray-600 leading-relaxed">
                Set organization-wide standards for call scripts, billing workflows, and patient communication, while allowing regional managers local flexibility where needed.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="group p-8 rounded-3xl bg-gray-50 border border-gray-100 hover:border-green-200 hover:shadow-xl hover:shadow-green-900/5 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <BarChart3 className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Enterprise Intelligence</h3>
              <p className="text-gray-600 leading-relaxed">
                Stop collating spreadsheets. Get real-time visibility into performance across every location, region, and the entire organization in a single dashboard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison / Feature Detail Section */}
      <section className="py-24 px-6 bg-slate-50 border-y border-gray-200">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-blue-600 text-xs font-bold uppercase tracking-wider mb-6 shadow-sm">
                <Settings className="w-4 h-4" />
                Operational Efficiency
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Standardize Best Practices <br />
                <span className="text-blue-600">Across All Locations</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Operational inconsistency kills DSO margins. With Asha, you ensure that every location handles insurance verification, billing, and scheduling with the same high level of precision.
              </p>
              
              <ul className="space-y-4">
                {[
                  "Unified patient experience regardless of location",
                  "Instant deployment of new workflows organization-wide",
                  "Eliminate training bottlenecks for front-desk staff",
                  "Reduce administrative overhead at the corporate level"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="relative">
              {/* Abstract Visual Representation of Multi-Location Management */}
              <div className="bg-white rounded-[2.5rem] p-8 shadow-2xl border border-gray-100 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500"></div>
                
                {/* Central Hub */}
                <div className="flex justify-center mb-8">
                  <div className="bg-slate-900 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-3 z-10">
                    <Building2 className="w-5 h-5 text-blue-400" />
                    <span className="font-bold">Corporate HQ Dashboard</span>
                  </div>
                </div>

                {/* Branches */}
                <div className="grid grid-cols-2 gap-4 relative">
                  {/* Connection Lines */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-8 w-px h-8 bg-gray-300"></div>
                  <div className="absolute top-0 left-1/4 right-1/4 -translate-y-4 h-px bg-gray-300"></div>
                  <div className="absolute top-0 left-1/4 -translate-y-4 h-4 w-px bg-gray-300"></div>
                  <div className="absolute top-0 right-1/4 -translate-y-4 h-4 w-px bg-gray-300"></div>

                  {/* Location Cards */}
                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex items-center gap-3">
                     <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-blue-600 shadow-sm"><Building2 className="w-5 h-5" /></div>
                     <div>
                       <div className="text-xs font-bold text-gray-500 uppercase">Location 1</div>
                       <div className="text-sm font-bold text-gray-900">Agents Active</div>
                     </div>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 flex items-center gap-3">
                     <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-purple-600 shadow-sm"><Building2 className="w-5 h-5" /></div>
                     <div>
                       <div className="text-xs font-bold text-gray-500 uppercase">Location 2</div>
                       <div className="text-sm font-bold text-gray-900">Agents Active</div>
                     </div>
                  </div>

                  <div className="bg-green-50 p-4 rounded-xl border border-green-100 flex items-center gap-3">
                     <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-green-600 shadow-sm"><Building2 className="w-5 h-5" /></div>
                     <div>
                       <div className="text-xs font-bold text-gray-500 uppercase">Location 3</div>
                       <div className="text-sm font-bold text-gray-900">Agents Active</div>
                     </div>
                  </div>

                  <div className="bg-orange-50 p-4 rounded-xl border border-orange-100 flex items-center gap-3">
                     <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-orange-600 shadow-sm"><Building2 className="w-5 h-5" /></div>
                     <div>
                       <div className="text-xs font-bold text-gray-500 uppercase">Location 4</div>
                       <div className="text-sm font-bold text-gray-900">Agents Active</div>
                     </div>
                  </div>
                </div>

                {/* Active Agent Overlay */}
                <div className="mt-8 bg-slate-50 rounded-xl p-4 border border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-2">
                       <img src="/assets/amy.png" className="w-8 h-8 rounded-full border-2 border-white" alt="Agent" />
                       <img src="/assets/ariel.png" className="w-8 h-8 rounded-full border-2 border-white" alt="Agent" />
                       <img src="/assets/leena.png" className="w-8 h-8 rounded-full border-2 border-white" alt="Agent" />
                    </div>
                    <span className="text-sm font-medium text-gray-600">Agents active across all your locations</span>
                  </div>
                  <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration & Security Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 gap-20">
            {/* Multi-PMS Migration */}
            <div>
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-6 text-indigo-600">
                <Network className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Multi-PMS Migration</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Acquisitions often mean inheriting different software stacks. We migrate locations from Dentrix, Eaglesoft, Open Dental, and Curve onto Asha PMS with a standardized rollout plan.
              </p>
              <div className="flex gap-4 opacity-70 grayscale hover:grayscale-0 transition-all">
                 {/* Placeholders for PMS logos or text */}
                 <span className="px-3 py-1 bg-gray-100 rounded text-xs font-bold text-gray-500">Dentrix</span>
                 <span className="px-3 py-1 bg-gray-100 rounded text-xs font-bold text-gray-500">Open Dental</span>
                 <span className="px-3 py-1 bg-gray-100 rounded text-xs font-bold text-gray-500">Eaglesoft</span>
                 <span className="px-3 py-1 bg-gray-100 rounded text-xs font-bold text-gray-500">Curve</span>
              </div>
            </div>

            {/* Enterprise Security */}
            <div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-6 text-blue-600">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Enterprise-Grade HIPAA Compliance</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Security built for scale. We provide encrypted data handling, role-based access controls for different organizational levels, and comprehensive audit trails to keep your group compliant.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-[2.5rem] p-12 text-center text-white shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/10 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
            
            <h2 className="text-3xl md:text-5xl font-bold mb-6 relative z-10">
              Ready to Scale Your DSO?
            </h2>
            <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto relative z-10">
              Book a consultation with our enterprise team to discuss your group's specific needs and see how Asha can drive growth across all your locations.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 relative z-10">
              <Link to="/book-demo">
                <button className="px-8 py-4 bg-white text-blue-600 rounded-xl font-bold text-lg hover:bg-blue-50 transition-colors shadow-lg">
                  Schedule Enterprise Demo
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DSOPage;
